// src/components/MedicalBilling.js
import React, { useState } from 'react';
import './MedicalBilling.css';

const MedicalBilling = () => {
  const [formData, setFormData] = useState({
    device: '',
    specialty: '',
    stage: '',
    fullName: '',
    phone: '',
    email: '',
    website: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const specialties = [
    'Cardiology', 'Neurology', 'Pediatrics', 'Dermatology', 
    'Orthopedics', 'Ophthalmology', 'Mental Health', 'Nephrology',
    'Podiatry', 'Allergy', 'Rehab', 'Neurosurgery'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://formspree.io/f/xrejwqgl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: 'Thank you! We will contact you shortly.'
        });
        setFormData({
          device: '',
          specialty: '',
          stage: '',
          fullName: '',
          phone: '',
          email: '',
          website: ''
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Something went wrong. Please try again or call us directly.'
      });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus({ type: '', message: '' }), 5000);
    }
  };

  // Medical Billing Services List
  const billingServices = [
    { title: 'Patient Demographics & Charges Entry', desc: 'Assistants identify the foundation of successful medical billing. They ensure that patients are billed accurately and efficiently.' },
    { title: 'Eligibility & Benefits Verification', desc: 'Verifying insurance eligibility and benefits before service delivery ensures that patients receive appropriate coverage. This step is crucial for ensuring smooth billing processes.' },
    { title: 'Prior Authorization Management', desc: 'Managing prior authorizations is essential for timely payments. Providers use our expertise to streamline the authorization process.' },
    { title: 'Claim Scrutinizing & Electronic Claim Submission', desc: 'We ensure accuracy during claim processing by reviewing all submitted claims. This step helps prevent denials and improves cash flow.' },
    { title: 'Payment Posting & Reconciliation', desc: 'Assistants verify financial reports and reconcile accounts. This step ensures accurate billing and payment.' },
    { title: 'Insurance Follow-Up & Claim Tracking', desc: 'Our team assists with claims follow-up and tracking. This step ensures that patients receive the correct coverage.' },
    { title: 'Denial Management & Appeals', desc: 'Denied claims don\'t have to mean lost revenue. We assist clients in appealing denied claims effectively.' },
    { title: 'Accounts Receivable (AR) Management', desc: 'Our AR management system ensures that we collect receivables quickly and efficiently.' },
    { title: 'Patient Billing & Statements', desc: 'We manage patient billing and statements. This step ensures that patients receive accurate statements and alerts.' },
    { title: 'Transparent Reporting & Insights', desc: 'We provide detailed reports to help you understand the performance of our services.' }
  ];

  return (
    <main className="medical-billing-page">
      {/* Hero Section */}
      <section className="medical-hero">
        <div className="container">
          <div className="medical-hero-content">
            <h1 className="medical-hero-title">
              Trusted Medical Billing Services <span className="highlight">in USA</span>
            </h1>
            <p className="medical-hero-description">
              Boost your practice's Medical Billing Service with Providers Care Billing LLC. We deliver accurate claim scrubbing, 
              free claims submission, and quicker reimbursement to reduce denials and improve cash flow so you can focus on patient care.
            </p>
            <div className="medical-hero-stats">
              <div className="hero-stat">
                <span className="stat-label">Premium Medical Billing Service</span>
                <span className="stat-value">Saving up to 2.45%</span>
              </div>
              <div className="hero-stat">
                <span className="stat-label">90/7/24</span>
                <span className="stat-value">Client Base for Faster Payments</span>
              </div>
            </div>
            <button className="hero-btn">Schedule An Appointment</button>
          </div>
        </div>
      </section>

      {/* Consultation Form Section */}
      <section className="consultation-form-section">
        <div className="container">
          <div className="form-wrapper">
            <h2 className="form-title">Get a free Consultation</h2>
            {submitStatus.message && (
              <div className={`status-message ${submitStatus.type}`}>{submitStatus.message}</div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <input type="text" name="device" placeholder="Select Device" value={formData.device} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <select name="specialty" value={formData.specialty} onChange={handleChange}>
                    <option value="">Select Specialty</option>
                    {specialties.map(spec => <option key={spec} value={spec}>{spec}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <input type="text" name="stage" placeholder="Select Stage" value={formData.stage} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <input type="text" name="fullName" placeholder="Full Name" value={formData.fullName} onChange={handleChange} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <input type="tel" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleChange} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group full-width">
                  <input type="url" name="website" placeholder="Website/Appointment" value={formData.website} onChange={handleChange} />
                </div>
              </div>
              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Get Started'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Network Section */}
      <section className="network-section">
        <div className="container">
          <div className="network-content">
            <h2 className="network-title">Join a Network of Clinics Growing with Confidence Nationwide</h2>
            <p className="network-text">
              Providers Care Billing LLC is a Top Medical Billing Company in USA and a HIPAA-compliant and BBB-accredited firm 
              providing end-to-end revenue cycle management (RCM) services. We help healthcare providers increase reimbursement, 
              reduce claim denials, and improve cash flow, acting as an extension of your practice so you can focus on quality patient care.
            </p>
            <button className="network-btn">Find Your Network →</button>
          </div>
        </div>
      </section>

      {/* Services Include Section */}
      <section className="services-include">
        <div className="container">
          <div className="services-include-header">
            <h2 className="section-title">Our Medical Billing Services Include</h2>
            <p className="section-subtitle">
              Providers Care Billing LLC understands the complexities of medical billing can be overwhelming. 
              That's why we're here to simplify the process for you.
            </p>
          </div>
          <div className="services-include-grid">
            {billingServices.map((service, index) => (
              <div className="service-include-card" key={index}>
                <h3 className="service-include-title">{service.title}</h3>
                <p className="service-include-desc">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialty Based Section */}
      <section className="specialty-based">
        <div className="container">
          <div className="specialty-based-content">
            <h2 className="specialty-based-title">Specialty Based Medical Billing Services</h2>
            <p className="specialty-based-text">
              At Providers Care Billing LLC, we understand that every medical specialty has unique billing, coding and 
              reimbursement requirements. A one-size-fits-all billing approach often leads to errors, denial, and delayed 
              payments. That's why we offer specialty-based medical billing services in USA tailored to the specific workflow, 
              documentation standards, and payer rules of each specialty.
            </p>
            <p className="specialty-based-text">
              We proudly work with all medical specialties, supporting practice of all sizes across the United States.
            </p>
            <button className="specialty-btn">Schedule An Appointment</button>
          </div>
        </div>
      </section>

      {/* Maximize Revenue Section */}
      <section className="maximize-revenue">
        <div className="container">
          <h2 className="revenue-title">Maximize Your Clinic's Revenue with Providers Care Billing</h2>
          <p className="revenue-subtitle">
            We know that healthcare specialties are unique as the patients they serve. That's why we offer specialized 
            medical billing services that are custom-tailored to meet the specific needs of each specialty.
          </p>
          <div className="revenue-features">
            <div className="revenue-feature">
              <h3>Dedicated Specialty Experts / Dedicated Account Manager</h3>
              <ul>
                <li>Specialty-Focused Billing Approach</li>
                <li>Personalized Support & Accountability</li>
                <li>Why Specialty-Based Billing Matters</li>
              </ul>
            </div>
            <div className="revenue-feature">
              <h3>Each practice is assigned:</h3>
              <ol>
                <li>Dedicated specialty trained billers who are experienced in your specific field.</li>
                <li>A dedicated account manager who acts as your single point of contact.</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="statistics-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">98.25%</div>
              <div className="stat-label">First Time Pass Rate</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">98.71%</div>
              <div className="stat-label">Submission Accuracy</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">$1B+</div>
              <div className="stat-label">Successfully Billed</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">98.54%</div>
              <div className="stat-label">Collection Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-medical">
        <div className="container">
          <h2 className="why-choose-title">Why Choose Our Medical Billing Company?</h2>
          <div className="why-choose-grid">
            <div className="why-card"><div className="why-icon">⚡</div><h3>Faster Reimbursement</h3><p>Our claims include management re-responsibility from the reimbursement.</p></div>
            <div className="why-card"><div className="why-icon">👤</div><h3>Dedicated Account Manager</h3><p>Personalised support for your practice.</p></div>
            <div className="why-card"><div className="why-icon">📈</div><h3>15-30% Revenue Improvement</h3><p>Optimised billing & follow ups.</p></div>
            <div className="why-card"><div className="why-icon">🔒</div><h3>HIPAA-Compliant Medical Billing</h3><p>Ensure data handling.</p></div>
            <div className="why-card"><div className="why-icon">🇺🇸</div><h3>U.S.-Based Medical Billing Services</h3><p>Close customer satisfaction & reliability.</p></div>
            <div className="why-card"><div className="why-icon">💰</div><h3>Transparent Pricing</h3><p>No hidden terms or long-term contracts.</p></div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="medical-process">
        <div className="container">
          <h2 className="process-title">Our Medical Billing Process</h2>
          <div className="process-list">
            <div className="process-item">✓ All claims are submitted on a daily basis</div>
            <div className="process-item">✓ All payments are posted on a daily basis</div>
            <div className="process-item">✓ All claims are rejected within 24 to 48 hours</div>
            <div className="process-item">✓ Eligibility is verified via location or location & days before the appointment</div>
            <div className="process-item">✓ In time authorization submission</div>
            <div className="process-item">✓ Weekly, bi-weekly and monthly analysis reports</div>
          </div>
        </div>
      </section>

      {/* Benefits of Outsourcing */}
      <section className="outsourcing-benefits">
        <div className="container">
          <div className="benefits-wrapper">
            <h2 className="benefits-title">Benefits of Outsourcing Medical Billing Services</h2>
            <div className="benefits-list">
              <div className="benefit-item">✓ Reduced Administrative Workload</div>
              <div className="benefit-item">✓ Faster reimbursement</div>
              <div className="benefit-item">✓ Improved compliance</div>
              <div className="benefit-item">✓ Lower operational costs</div>
              <div className="benefit-item">✓ Increased practice profitability</div>
              <div className="benefit-item">✓ Increase revenue by 15% to 30%</div>
            </div>
            <button className="demo-btn-free">Get a Free Demo</button>
          </div>
        </div>
      </section>

      {/* Trusted Services Section */}
      <section className="trusted-services">
        <div className="container">
          <div className="trusted-content">
            <h2>Trusted Medical Billing Services in the USA</h2>
            <p>Whether you're a solo provider or a multi-specialty practice, Providers Care Billing LLC delivers scalable, reliable, and HIPAA-compliant medical billing services tailored to your needs.</p>
            <button className="trusted-btn">Schedule a Free Medical Billing Consultation Today</button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default MedicalBilling;